import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SectionTitle from "../SectionTitle/SectionTitle";
import axios from "axios";
import { FaMapMarkerAlt, FaExternalLinkAlt, FaWarehouse, FaRulerCombined, FaChevronDown, FaChevronUp } from "react-icons/fa";

const settings = {
    dots: false,
    arrows: true,
    speed: 1000,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    responsive: [
        {
            breakpoint: 1400,
            settings: {
                slidesToShow: 3,
                slidesToScroll: 1,
                infinite: true,
            }
        },
        {
            breakpoint: 1200,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                infinite: true,
                centerMode: false,
            }
        },
        {
            breakpoint: 991,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
                centerMode: false,
            }
        },
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                centerMode: false,
            }
        },
        {
            breakpoint: 480,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                centerMode: false,
            }
        }
    ]
};

const stripHtmlTags = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, ''); // removes all HTML tags
};

const WarehouseSection = () => {
    const [warehouses, setWarehouses] = useState([]);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [maxHeight, setMaxHeight] = useState(0);
    const itemRefs = useRef([]);
    const apiUrl = process.env.REACT_APP_PUBLIC_API_URL;
    
    useEffect(() => {
        const getWarehouses = async () => {
            try {
                const res = await axios.get(`${apiUrl}/api/warehouses-list`);
                setWarehouses(res.data);
            } catch (error) {
                console.log(error)
            }
        }
        getWarehouses();
    }, [apiUrl]);

    useEffect(() => {
        if (warehouses.length > 0 && itemRefs.current.length > 0) {
            // Wait for DOM to render images first
            setTimeout(() => {
            const heights = itemRefs.current.map((el) => el?.offsetHeight || 0);
            const tallest = Math.max(...heights);
            setMaxHeight(tallest);
            }, 300);
        }
    }, [warehouses]);

    const toggleDescription = (index) => {
        setExpandedCards(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    if (!warehouses) return null;

    return (
        <section 
            className="wpo-service-section-s2 section-padding" 
            style={{ 
                background: "#f8fafc",
                paddingTop: "40px",   // reduce vertical padding
                paddingBottom: "40px"
            }}
        >
            <div className="container">
                <SectionTitle subTitle={'Storage Facilities'} Title={'Our Warehouses'} />
                <div className="row-grid wpo-service-slider-s2">
                    <Slider {...settings}>
                        {warehouses.map((warehouse, index) => {
                            const isHovered = hoveredIndex === index;
                            const isExpanded = expandedCards[index];
                            const description = stripHtmlTags(warehouse.description) || "No description available";
                            const shortDesc = description.length > 100 ? description.slice(0, 100) + "..." : description;
                            const showReadMore = description.length > 100;

                            return (
                                <div className="grid" key={index}>
                                    <div 
                                        ref={el => itemRefs.current[index] = el}
                                        style={{
                                            background: "#ffffff",
                                            borderRadius: "20px",
                                            overflow: "hidden",
                                            transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                            boxShadow: isHovered 
                                                ? "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px #3b82f6"
                                                : "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px #e2e8f0",
                                            transform: isHovered ? "translateY(-8px) scale(1.02)" : "translateY(0) scale(1)",
                                            margin: "20px 15px",
                                            cursor: "pointer",
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative",
                                            height: maxHeight && !isExpanded ? `${maxHeight}px` : "auto",
                                            minHeight: maxHeight ? `${maxHeight}px` : "auto",
                                        }}
                                        onMouseEnter={() => setHoveredIndex(index)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                    >
                                        {/* Image Section */}
                                        <div style={{
                                            position: "relative",
                                            height: "180px",
                                            overflow: "hidden",
                                        }}>
                                            <img 
                                                src={`${apiUrl}/storage/`+warehouse.image} 
                                                alt={warehouse.name}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    transform: isHovered ? "scale(1.1)" : "scale(1)",
                                                    transition: "transform 0.6s ease",
                                                    display: "block",
                                                }}
                                            />
                                            {/* Gradient Overlay */}
                                            <div style={{
                                                position: "absolute",
                                                bottom: "0",
                                                left: "0",
                                                width: "100%",
                                                height: "50%",
                                                background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)",
                                            }} />
                                            {/* Warehouse Icon Badge */}
                                            <div style={{
                                                position: "absolute",
                                                top: "16px",
                                                left: "16px",
                                                background: "rgba(255, 255, 255, 0.95)",
                                                padding: "8px",
                                                borderRadius: "12px",
                                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                            }}>
                                                <FaWarehouse size={20} color="#3b82f6" />
                                            </div>
                                        </div>

                                        {/* Warehouse Name - Smaller background with border fill */}
                                        <div style={{
                                            position: "relative",
                                            zIndex: 5,
                                            marginTop: "-20px", // Pulls it up over the image
                                            marginBottom: "20px",
                                            padding: "0 24px",
                                        }}>
                                            <div style={{
                                                background: "#ffffff",
                                                padding: "12px 20px",
                                                borderRadius: "12px",
                                                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12), 0 3px 8px rgba(0, 0, 0, 0.08)",
                                                border: isHovered 
                                                    ? "2px solid #3b82f6" 
                                                    : "1px solid #f1f5f9",
                                                transform: isHovered ? "translateY(-3px) scale(1.03)" : "translateY(0) scale(1)",
                                                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                                textAlign: "center",
                                                position: "relative",
                                                overflow: "hidden",
                                            }}>
                                                {/* Animated border fill effect */}
                                                <div style={{
                                                    position: "absolute",
                                                    top: "0",
                                                    left: "0",
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                                                    opacity: isHovered ? 0.08 : 0,
                                                    transition: "opacity 0.3s ease",
                                                    zIndex: 1,
                                                }} />
                                                <h2 style={{
                                                    fontSize: "16px",
                                                    fontWeight: "700",
                                                    color: isHovered ? "#3b82f6" : "#1e293b",
                                                    margin: "0",
                                                    lineHeight: "1.3",
                                                    letterSpacing: "-0.3px",
                                                    position: "relative",
                                                    zIndex: 2,
                                                    transition: "all 0.3s ease",
                                                }}>
                                                    {warehouse.name}
                                                </h2>
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div style={{ 
                                            padding: "0 24px 24px 24px",
                                            marginTop: "-8px", // Adjust to close the gap
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                        }}>
                                            {/* Description with Expandable Feature */}
                                            <div style={{
                                                flex: 1,
                                                marginBottom: "16px",
                                            }}>
                                                <p style={{
                                                    fontSize: "14px",
                                                    color: "#64748b",
                                                    lineHeight: "1.6",
                                                    margin: "0 0 12px 0",
                                                    transition: "all 0.3s ease",
                                                }}>
                                                    {isExpanded ? description : shortDesc}
                                                </p>
                                                
                                                {/* Read More/Less Button */}
                                                {showReadMore && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleDescription(index);
                                                        }}
                                                        style={{
                                                            background: "transparent",
                                                            border: "none",
                                                            color: "#3b82f6",
                                                            fontSize: "13px",
                                                            fontWeight: "600",
                                                            cursor: "pointer",
                                                            padding: "4px 8px",
                                                            borderRadius: "6px",
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            transition: "all 0.2s ease",
                                                            marginBottom: "8px",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.background = "rgba(59, 130, 246, 0.1)";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.background = "transparent";
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <>
                                                                <FaChevronUp size={10} />
                                                                Show Less
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaChevronDown size={10} />
                                                                Read More
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Details Grid */}
                                            <div style={{
                                                display: "grid",
                                                gap: "12px",
                                                marginBottom: "20px",
                                            }}>
                                                {/* Location */}
                                                {(warehouse.location || warehouse.address) && (
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: "10px",
                                                    }}>
                                                        <FaMapMarkerAlt size={14} color="#ef4444" style={{ marginTop: "2px", flexShrink: 0 }} />
                                                        <span style={{
                                                            fontSize: "13px",
                                                            color: "#475569",
                                                            lineHeight: "1.4",
                                                        }}>
                                                            {warehouse.location ? (
                                                                <>
                                                                    <strong style={{ color: "#1e293b" }}>{warehouse.location}</strong>
                                                                    {warehouse.address ? ` (${warehouse.address})` : null}
                                                                </>
                                                            ) : (
                                                                <>{warehouse.address}</>
                                                            )}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Size */}
                                                {warehouse.size && (
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}>
                                                        <FaRulerCombined size={14} color="#10b981" style={{ flexShrink: 0 }} />
                                                        <span style={{
                                                            fontSize: "13px",
                                                            color: "#475569",
                                                        }}>
                                                            <strong style={{ color: "#1e293b" }}>Size:</strong> {warehouse.size}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Map Link */}
                                            {warehouse.map_url && (
                                                <a
                                                    href={warehouse.map_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        padding: "10px 20px",
                                                        borderRadius: "8px",
                                                        background: isHovered
                                                        ? "linear-gradient(135deg, #0a2a6b, #3b82f6)" // dark blue → light blue
                                                        : "#f1f5f9", // default light gray
                                                        color: isHovered ? "#ffffff" : "#3b82f6",
                                                        textDecoration: "none",
                                                        fontSize: "14px",
                                                        fontWeight: "600",
                                                        transition: "all 0.3s ease",
                                                        border: "1px solid #e2e8f0",
                                                        boxShadow: isHovered
                                                        ? "0 6px 18px rgba(59, 130, 246, 0.4)"
                                                        : "0 2px 8px rgba(0, 0, 0, 0.05)",
                                                        transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                                                        marginTop: "auto",
                                                    }}
                                                >
                                                    <FaExternalLinkAlt size={12} />
                                                    View on Map
                                                </a>
                                            )}
                                        </div>

                                        {/* Hover Border Effect */}
                                        <div style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            right: "0",
                                            bottom: "0",
                                            border: "2px solid #3b82f6",
                                            borderRadius: "20px",
                                            opacity: isHovered ? 1 : 0,
                                            transition: "opacity 0.3s ease",
                                            pointerEvents: "none",
                                        }} />
                                    </div>
                                </div>
                            );
                        })}
                    </Slider>
                </div>
            </div>
        </section>
    );
}

export default WarehouseSection;